import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

export const CreationEntry = () => {
  const navigate = useNavigate()
  const textareaRef = useRef(null)
  const [storyContent, setStoryContent] = useState('')
  const [creationMode, setCreationMode] = useState('intelligent')
  const [createType, setCreateType] = useState('world')

  const handleStartCreation = async () => {
    if (!storyContent.trim()) {
      toast.error('请输入你的创意想法')
      return
    }

    if (createType === 'story') {
      navigate('/create', { state: { content: storyContent, mode: creationMode, type: 'story' } })
    } else if (createType === 'character') {
      if (creationMode === 'intelligent') {
        sessionStorage.setItem('character-creation-description', storyContent)
        navigate('/play-create')
      } else {
        // 自动生成模式需要调用API，这里先跳转到创建页面
        navigate('/play-create', { state: { autoCreate: true, description: storyContent } })
      }
    } else if (createType === 'world') {
      if (creationMode === 'intelligent') {
        sessionStorage.setItem('world-creation-description', storyContent)
        navigate('/world-simulator/create')
      } else {
        navigate('/world-simulator/create', { state: { autoCreate: true, description: storyContent } })
      }
    }
  }

  // 自动调整textarea高度
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      // 重置高度以自动调整
      textarea.style.height = 'auto'
      // 计算内容高度
      const scrollHeight = textarea.scrollHeight
      const maxHeight = 10 * 1.5 * 16 + 24 // 10行 * 1.5行高 * 16px + 24px padding
      // 设置高度，但不超过最大值
      textarea.style.height = Math.min(scrollHeight, maxHeight) + 'px'
    }
  }, [storyContent])

  return (
    <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
      {/* 标题和描述 - 更简洁 */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-white mb-1">开始创作</h3>
        <p className="text-sm text-dark-400">一句话描述你的创意想法</p>
      </div>
      
      {/* 主输入框 */}
      <div className="w-full bg-dark-700/50 backdrop-blur-sm rounded-lg p-1 border border-dark-600 mb-4">
        <textarea
          ref={textareaRef}
          value={storyContent}
          onChange={(e) => setStoryContent(e.target.value)}
          placeholder={
            createType === 'story' 
              ? '例如：一个关于星际旅行的冒险故事...'
              : createType === 'character'
              ? '例如：一个温柔善良的魔法师...'
              : '例如：一个明末天启年间的历史模拟世界...'
          }
          className="w-full bg-transparent border-none rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-white placeholder-dark-400 resize-none"
          style={{
            minHeight: '44px',
            maxHeight: '264px', // 10行的高度（10 * 1.5rem * 16px + 24px padding）
            lineHeight: '1.5rem',
            overflowY: 'auto',
            wordWrap: 'break-word',
            whiteSpace: 'pre-wrap'
          }}
          onKeyDown={(e) => {
            // 如果按Ctrl+Enter或Cmd+Enter，提交
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && storyContent.trim()) {
              e.preventDefault()
              handleStartCreation()
            }
          }}
        />
      </div>
      
      {/* 底部操作栏 - 整合所有按钮 */}
      <div className="flex flex-wrap items-center gap-3">
        {/* 左侧：创作类型选择 */}
        <div className="flex gap-2 flex-1 min-w-0">
          <button
            onClick={() => {
              setCreateType('story')
              setCreationMode('intelligent')
            }}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
              createType === 'story'
                ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white shadow-lg'
                : 'bg-dark-700/50 text-white/70 hover:text-white hover:bg-dark-700 border border-dark-600'
            }`}
          >
            <span>📖</span>
            <span>故事</span>
          </button>
          
          <button
            onClick={() => {
              setCreateType('character')
              setCreationMode('intelligent')
            }}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
              createType === 'character'
                ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                : 'bg-dark-700/50 text-white/70 hover:text-white hover:bg-dark-700 border border-dark-600'
            }`}
          >
            <span>👤</span>
            <span>角色</span>
          </button>
          
          <button
            onClick={() => {
              setCreateType('world')
              setCreationMode('intelligent')
            }}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
              createType === 'world'
                ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                : 'bg-dark-700/50 text-white/70 hover:text-white hover:bg-dark-700 border border-dark-600'
            }`}
          >
            <span>🌍</span>
            <span>世界</span>
          </button>
        </div>

        {/* 中间：模式选择 */}
        <div className="flex gap-2">
          <button
            onClick={() => setCreationMode('intelligent')}
            className={`px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
              creationMode === 'intelligent'
                ? 'bg-primary text-white shadow-lg border-2 border-white/30'
                : 'text-dark-300 hover:text-white hover:bg-dark-700/50 bg-dark-700/30 border border-dark-600'
            }`}
          >
            智能引导
          </button>
          <button
            onClick={() => setCreationMode('auto')}
            className={`px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
              creationMode === 'auto'
                ? 'bg-primary text-white shadow-lg border-2 border-white/30'
                : 'text-dark-300 hover:text-white hover:bg-dark-700/50 bg-dark-700/30 border border-dark-600'
            }`}
          >
            自动生成
          </button>
        </div>

        {/* 右侧：开始创作按钮 */}
        <button 
          onClick={handleStartCreation}
          disabled={!storyContent.trim()}
          className="bg-gradient-to-r from-primary to-secondary hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap border-2 border-primary/50 shadow-lg shadow-primary/30"
        >
          {createType === 'story' ? '开始创作' : createType === 'character' ? '创建角色' : '创建世界'}
        </button>
      </div>
    </div>
  )
}

